import { AuthStorageWrapper } from '../AuthStorageWrapper';
import { OIDCEndpointsTableEntry, OIDCEndpointsTableKey, OIDCDiscoveryResponse } from './TypeDefinitions';
import { storageConstants } from '../AuthConstants';
import { logConstants } from "../../utils/Constants";
import { Logger } from '../Logger';

/**
 * Singleton class that handles operations on the `OIDCEndpoints` table in IndexedDB.
 */
export default class OIDCEndpointsManager {
	private static instance: OIDCEndpointsManager;

	static getInstance(): OIDCEndpointsManager {
		if (!OIDCEndpointsManager.instance) {
			OIDCEndpointsManager.instance = new OIDCEndpointsManager();
		}
		return OIDCEndpointsManager.instance;
	}

	/**
	 * Gets OIDC Endpoints corresponding to the given key.
	 *
	 * @param {OIDCEndpointsTableKey} key
	 * @returns {(Promise<OIDCEndpointsTableEntry | null>)} object of type `OIDCTokensTableEntry` if the entry was found in the table, `null` otherwise.
	 * @memberof OIDCEndpointsManager
	 */
	async retrieveOIDCEndpoints(key: OIDCEndpointsTableKey): Promise<OIDCEndpointsTableEntry | null> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		const oidcEndpoints = (await authStorage.readEntry(storageConstants.OIDC_ENDPOINTS_TABLE, [
			key.serviceURL,
			key.authDomain,
			key.referenceUri,
		])) as OIDCEndpointsTableEntry;
		if (oidcEndpoints) {
			return oidcEndpoints;
		}
		return null;
	}

	/**
	 * Gets all the entries from the OIDC endpoints table
	 * @returns all the endpoint table entries
	 * @memberof OIDCEndpointsManager
	 */
	async retrieveAllOIDCEndPointTableEntries(): Promise<OIDCEndpointsTableEntry[]> {
        const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
        return (await authStorage.readAll(
			storageConstants.OIDC_ENDPOINTS_TABLE
        )) as OIDCEndpointsTableEntry[];
    }

	/**
	 * Stores the endpoints obtained from OIDC Discovery in the `OIDCEndpoints` table in IndexedDB.
	 *
	 * @param {OIDCEndpointsTableKey} key object of type `OIDCEndpointsTableKey` containing authDomain, referenceUri, and serviceUrl.
	 * @param {OIDCDiscoveryResponse} endpoints object of type OIDCDiscoveryResponse containing the discovered endpoints.
	 * @returns {Promise<void>}
	 * @memberof OIDCEndpointsManager
	 */
	async saveOIDCEndpoints(key: OIDCEndpointsTableKey, endpoints: OIDCDiscoveryResponse): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		const tableEntry = {
			...key,
			...endpoints,
		};
		Logger.getInstance().write(
			logConstants.INFO,
			'Storing OIDC Endpoints for service url = ' +
				key.serviceURL +
				', auth domain = ' +
				key.authDomain +
				', reference uri = ' +
				key.referenceUri
		);
		await authStorage.updateEntry(storageConstants.OIDC_ENDPOINTS_TABLE, tableEntry);
	}

	/**
	 * Deletes the OIDC Endpoints corresponding to the given key.
	 *
	 * @param {OIDCEndpointsTableKey} key object of type `OIDCEndpointsTableKey`.
	 * @returns {Promise<void>}
	 * @memberof OIDCEndpointsManager
	 */
	async deleteOIDCEndpoints(key: OIDCEndpointsTableKey): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		Logger.getInstance().write(
			logConstants.INFO,
			'Deleting OIDC Endpoints for reference uri = ' +
				key.referenceUri +
				', auth domain = ' +
				key.authDomain +
				', service url = ' +
				key.serviceURL
		);
		await authStorage.deleteEntry(storageConstants.OIDC_ENDPOINTS_TABLE, [
			key.serviceURL,
			key.authDomain,
			key.referenceUri,
		]);
	}
}
